import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import MultiCheckbox from "@/components/MultiCheckbox";
import { SUBJECTS, CLASS_LEVELS } from "@/constants/options";
import { Plus, Trash2, Save, User, BookOpen, Mail, Phone } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = () => ({ name: "", class_level: "", subjects: [], address: "" });

export default function FamilyProfile() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [children, setChildren] = useState([empty()]);
  const [savingName, setSavingName] = useState(false);
  const [savingChildren, setSavingChildren] = useState(false);

  useEffect(() => {
    if (user) setName(user.name || "");
    (async () => {
      try {
        const { data } = await api.get("/family/profile");
        if (data.children?.length) setChildren(data.children.map((c) => ({
          name: c.name || "",
          class_level: c.class_level || "",
          subjects: c.subjects || [],
          address: c.address || "",
        })));
      } catch {}
    })();
    // eslint-disable-next-line
  }, [user?.id]);

  const update = (i, key, val) => {
    setChildren((cs) => cs.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)));
  };
  const add = () => setChildren((cs) => [...cs, empty()]);
  const remove = (i) => setChildren((cs) => cs.filter((_, idx) => idx !== i));

  const saveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Ad Soyad boş olamaz");
    setSavingName(true);
    try {
      await api.put("/user/me", { name });
      await refresh();
      toast.success("Bilgilerin güncellendi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSavingName(false); }
  };

  const saveChildren = async (e) => {
    e.preventDefault();
    for (const c of children) {
      if (!c.name || !c.class_level || !c.address) return toast.error("Tüm çocuk bilgilerini doldur");
      if (!c.subjects.length) return toast.error("Her çocuk için en az bir ders seç");
    }
    setSavingChildren(true);
    try {
      await api.post("/family/profile", { children });
      toast.success("Çocuk bilgileri güncellendi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSavingChildren(false); }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="reveal">
          <span className="text-xs uppercase tracking-widest text-[color:var(--brand)] font-semibold">Profilim</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter" data-testid="family-profile-title">Aile Profili</h1>
          <p className="mt-3 text-slate-500">Kişisel bilgilerini ve çocuklarının detaylarını güncelle.</p>
        </div>

        {/* User info */}
        <form onSubmit={saveName} className="mt-10 border border-slate-200 rounded-2xl p-6 sm:p-8 bg-white reveal">
          <div className="flex items-center gap-2 mb-4">
            <User size={16} className="text-[color:var(--brand)]" />
            <h2 className="font-display text-xl font-semibold">Hesap bilgileri</h2>
          </div>

          <div className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium">Ad Soyad</Label>
              <Input required data-testid="family-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-12" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium flex items-center gap-1"><Mail size={12} /> E-posta</Label>
                <Input value={user?.email || ""} disabled className="mt-2 h-12 bg-slate-50" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium flex items-center gap-1"><Phone size={12} /> Telefon</Label>
                <Input value={user?.phone || ""} disabled className="mt-2 h-12 bg-slate-50" />
              </div>
            </div>
            <p className="text-xs text-slate-400">E-posta ve telefon değişikliği için destek ekibiyle iletişime geç.</p>
          </div>

          <Button type="submit" disabled={savingName} data-testid="save-name-btn"
            className="mt-6 h-11 px-6 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white">
            <Save size={14} className="mr-2" /> {savingName ? "Kaydediliyor..." : "Bilgileri Güncelle"}
          </Button>
        </form>

        {/* Children */}
        <form onSubmit={saveChildren} className="mt-10 space-y-6">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-[color:var(--brand)]" />
            <h2 className="font-display text-xl font-semibold">Çocuklar</h2>
          </div>

          {children.map((c, i) => (
            <div key={i} className="border border-slate-200 rounded-2xl p-6 sm:p-8 bg-white" data-testid={`edit-child-block-${i}`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-lg font-semibold">Çocuk {i + 1}</h3>
                {children.length > 1 && (
                  <button type="button" onClick={() => remove(i)} className="text-sm text-slate-500 hover:text-red-600 inline-flex items-center gap-1" data-testid={`edit-remove-child-${i}`}>
                    <Trash2 size={14} /> Kaldır
                  </button>
                )}
              </div>
              <div className="space-y-5">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium">Ad Soyad</Label>
                  <Input required data-testid={`edit-child-name-${i}`} value={c.name} onChange={(e) => update(i, "name", e.target.value)} className="mt-2 h-12" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium">Sınıf</Label>
                  <Select value={c.class_level} onValueChange={(v) => update(i, "class_level", v)}>
                    <SelectTrigger className="mt-2 h-12" data-testid={`edit-child-class-${i}`}>
                      <SelectValue placeholder="Sınıf seç" />
                    </SelectTrigger>
                    <SelectContent>
                      {CLASS_LEVELS.map((cl) => <SelectItem key={cl} value={cl}>{cl}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3 block">Almak istediği dersler</Label>
                  <MultiCheckbox
                    options={SUBJECTS}
                    values={c.subjects}
                    onChange={(v) => update(i, "subjects", v)}
                    testidPrefix={`edit-child-subject-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium">Dersin yapılacağı adres</Label>
                  <Input required data-testid={`edit-child-address-${i}`} value={c.address} onChange={(e) => update(i, "address", e.target.value)} className="mt-2 h-12" />
                </div>
              </div>
            </div>
          ))}

          <button type="button" onClick={add} data-testid="edit-add-child-btn"
            className="w-full border border-dashed border-slate-300 rounded-2xl py-5 text-sm font-medium text-slate-700 hover:border-[color:var(--brand)] hover:bg-[color:var(--brand)]/5 transition-colors inline-flex items-center justify-center gap-2">
            <Plus size={16} /> Çocuk Ekle
          </button>

          <Button type="submit" disabled={savingChildren} data-testid="save-children-btn"
            className="w-full h-12 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white text-base">
            <Save size={14} className="mr-2" /> {savingChildren ? "Kaydediliyor..." : "Çocuk Bilgilerini Kaydet"}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
